import React, { useState } from 'react';
import { Tag, Heading, Lead, PageHero, Section, BtnPrimary } from '../components/UI';
import styles from './AppDev.module.css';


const TYPES = [
  { t: 'Custom Software Solutions',  d: 'Purpose-built software tailored specifically to your business processes and goals.' },
  { t: 'Web Applications',            d: 'Scalable, browser-based applications with modern tech stacks.' },
  { t: 'Mobile Applications',         d: 'iOS, Android, and cross-platform apps for all devices.' },
  { t: 'Enterprise Software',         d: 'Large-scale systems handling high data volumes, users, and transactions.' },
  { t: 'SaaS Products',               d: 'Subscription-based cloud software designed for multi-tenant deployment.' },
  { t: 'E-commerce Platforms',        d: 'Complete online commerce solutions with payments, inventory, and CMS.' },
  { t: 'Cloud-Based Applications',    d: 'AWS, Azure, and Google Cloud native apps built for scalability.' },
];
const SERVICES = [
  {
    n: '01', 
    t: 'Custom Application Development',
    d: 'We specialize in building applications from the ground up, crafted specifically for your organization. Personalized to address your exact challenges, integrate seamlessly with your existing systems, and adapt as your business evolves — ensuring flexibility, efficiency, and a competitive edge.',
   
  },
  {
    n: '02',
    t: 'Web Application Development',
    d: 'Modern, responsive, and secure web applications built using the latest technologies. From portals to complex enterprise CRM systems, SaaS products, and e-commerce platforms — we deliver scalable solutions that drive real business results.',
  },
  {
    n: '03',
    t: 'Mobile Application Development',
    d: 'High-performance apps for Android and iOS. Cross-platform solutions built with Flutter and React Native that work seamlessly across multiple operating systems with smooth performance and engaging UX.',
  },
  {
    n: '04',
    t: 'Enterprise Application Solutions',
    d: 'Streamline business processes across finance, HR, supply chain, and CRM with powerful enterprise-grade applications designed for efficiency, high-volume transactions, and sustainable long-term growth.',
  },
  {
    n: '05',
    t: 'UI/UX Design',
    d: 'Clean, intuitive, and engaging interfaces that enhance user satisfaction and increase retention. We put users at the center of every design decision, from wireframes to final pixel-perfect deliverables.',
  },
  {
    n: '06',
    t: 'Application Maintenance & Support',
    d: 'Ongoing monitoring, updates, troubleshooting, and optimization to keep your application secure, compatible with new technologies, and capable of meeting changing business needs over the long term.',
  },
];


const PROCESS = [
  { n: '01', t: 'Requirement Analysis', d: 'Deep discovery of your business needs, goals, and technical constraints.' },
  { n: '02', t: 'Planning & Strategy',  d: 'Architecture planning, tech stack selection, and project roadmap.' },
  { n: '03', t: 'UI/UX Design',         d: 'User-centered interface design with prototyping and validation.' },
  { n: '04', t: 'Development',          d: 'Agile sprint-based development with continuous integration.' },
  { n: '05', t: 'Testing & QA',         d: 'Comprehensive automated and manual testing across all layers.' },
  { n: '06', t: 'Deployment',           d: 'Smooth production deployment with zero-downtime strategies.' },
  { n: '07', t: 'Support & Scale',      d: 'Ongoing monitoring, updates, and performance optimization.' },
];

const TECH = [
  { label: 'Frontend',  items: ['HTML5', 'CSS3', 'JavaScript', 'React', 'Angular', 'Vue.js'],   dark: true },
  { label: 'Backend',   items: ['Node.js', 'Python', 'PHP', 'Java', 'Spring Boot', 'Django'],   dark: false },
  { label: 'Mobile',    items: ['Flutter', 'React Native', 'Swift', 'Kotlin'],                   dark: true },
  { label: 'Database',  items: ['MySQL', 'MongoDB', 'PostgreSQL', 'Redis', 'Elasticsearch'],     dark: false },
  { label: 'Cloud',     items: ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],         dark: true },
];

const PLATFORMS = [
  { icon: '🌐', label: 'Web',         tech: 'React, Angular, Vue' },
  { icon: '📱', label: 'iOS',          tech: 'Swift, React Native' },
  { icon: '🤖', label: 'Android',      tech: 'Kotlin, Flutter' },
  { icon: '☁️', label: 'Cloud',        tech: 'AWS, Azure, GCP' },
  { icon: '🏢', label: 'Enterprise',   tech: 'Java, .NET, SAP' },
  { icon: '🔌', label: 'APIs',         tech: 'REST, GraphQL, gRPC' },
];

export default function AppDev() {
  const [active, setActive] = useState(null);

  return (
    <>
      <PageHero
        tag="Services"
        title="Build Smart, Scalable & Future-Ready Applications"
        subtitle="We design and develop high-performance applications tailored to your business goals — seamless functionality, intuitive design, and robust performance across all platforms."
      />
 

      {/* ── Services ───────────────────────────────────────────── */}
      <Section variant="paper">
        <Tag>What We Offer</Tag>
        <Heading>Our Application Services</Heading>
        <div className={styles.servicesGrid}>
          {SERVICES.map((svc, i) => (
            <div
              key={i}
              className={`${styles.svcCard} ${svc.featured ? styles.svcFeatured : ''}`}
            >
              <div className={styles.svcNum}>
                {svc.featured ? 'Flagship Offering' : `Service ${svc.n}`}
              </div>
              <h3 className={styles.svcTitle}>{svc.t}</h3>
              <p className={styles.svcDesc}>{svc.d}</p>
              {svc.features && (
                <div className={styles.featuresGrid}>
                  {svc.features.map((f, j) => (
                    <div key={j} className={styles.featureRow}>
                      <span className={styles.featureDot} style={{
                        background: ['#1a3cff','#ff4d2e','#10b981','#f59e0b'][j]
                      }} />
                      {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

       {/* ── Software Types ─────────────────────────────────────── */}
       <Section variant="paper">
        <Tag>What We Build</Tag>
        <Heading>Types of Software We Develop</Heading>
        <Lead>From concept to deployment, we cover every category of modern software.</Lead>
        <div className={styles.typesGrid}>
          {TYPES.map((type, i) => (
            <div key={i} className={styles.typeCard}>
              <div className={styles.typeNum}>0{i + 1}</div>
              <h3 className={styles.typeTitle}>{type.t}</h3>
              <p className={styles.typeDesc}>{type.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Process ────────────────────────────────────────────── */}
      <Section variant="white">
        <Tag>Our Methodology</Tag>
        <Heading>Development Process</Heading>
        <Lead>A structured, proven approach that ensures quality delivery from day one.</Lead>
        <div className={styles.processGrid}>
          {PROCESS.map((step, i) => (
            <div key={i} className={styles.processStep}>
              <div className={styles.processNum}>{step.n}</div>
              <h4 className={styles.processTitle}>{step.t}</h4>
              <p className={styles.processDesc}>{step.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Tech Stack ─────────────────────────────────────────── */}
      <Section variant="ink">
        <Tag light>Technologies</Tag>
        <Heading light>Our Tech Stack</Heading>
        <Lead light>We work with the tools and frameworks that best fit your project's needs.</Lead>
        <div className={styles.techStack}>
          {TECH.map((row, i) => (
            <div key={i} className={styles.techRow}>
              <div className={styles.techLabel}>{row.label}</div>
              <div className={styles.techItems}>
                {row.items.map((item, j) => (
                  <span key={j} className={`${styles.techBadge} ${row.dark ? styles.techBadgeDark : styles.techBadgeOutline}`}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      
      {/* ── Why Choose Our Dev ─────────────────────────────────── */}
      <Section variant="paper">
        <div className={styles.bottomGrid}>
          <div>
            <Tag>Why Choose Us</Tag>
            <Heading>Built for Performance & Scale</Heading>
            <Lead>Every solution we build is crafted with long-term success in mind.</Lead>
            <ul className={styles.benefitList}>
              {[
                'Experienced Development Team across all platforms',
                'Scalable & Secure architecture by design',
                'Agile methodology for faster, adaptive delivery',
                'On-Time Delivery with milestone tracking',
                'Cost-Effective Solutions without quality compromise',
                '24/7 Post-launch Support & Maintenance',
              ].map((item, i) => (
                <li key={i} className={styles.benefitItem}>
                  <span className={styles.benefitDot} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.industriesServed}>
            <div className={styles.indCard}>
              <h4 className={styles.indTitle}>Industries We Serve</h4>
              {['Healthcare', 'E-commerce', 'Education', 'Finance', 'Logistics', 'Real Estate'].map((ind, i) => (
                <div key={i} className={styles.indItem}>{ind}</div>
              ))}
            </div>
            <BtnPrimary to="/contact-us" style={{ marginTop: '1.5rem', width: '100%', justifyContent: 'center' }}>
              Start Your Project →
            </BtnPrimary>
          </div>
        </div>
      </Section>

      {/* ── Platforms ──────────────────────────────────────────── */}
      {/* <Section variant="white">
        <Tag>Platforms & Technologies</Tag>
        <Heading>We Build Across Every Platform</Heading>
        <Lead>From web to mobile to enterprise — our expertise spans the full technology landscape.</Lead>
        <div className={styles.platformsGrid}>
          {PLATFORMS.map((p, i) => (
            <div key={i} className={styles.platformCard}>
              <div className={styles.platformIcon}>{p.icon}</div>
              <div className={styles.platformLabel}>{p.label}</div>
              <div className={styles.platformTech}>{p.tech}</div>
            </div>
          ))}
        </div>
      </Section> */}

      {/* ── Process ────────────────────────────────────────────── */}
      {/* <Section variant="ink">
        <Tag light>Our Process</Tag>
        <Heading light>How We Deliver</Heading>
        <Lead light>A collaborative, transparent process from first brief to production launch.</Lead>
        <div className={styles.processStrip}>
          {['Discovery','Design','Develop','Test','Launch','Support'].map((step, i) => (
            <div key={i} className={styles.processItem}>
              <div className={styles.processCircle}>{i + 1}</div>
              <div className={styles.processConnector} style={{ display: i === 5 ? 'none' : 'block' }} />
              <div className={styles.processLabel}>{step}</div>
            </div>
          ))}
        </div>
      </Section> */}

      {/* ── CTA ────────────────────────────────────────────────── */}
      {/* <Section variant="paper" style={{ textAlign: 'center' }}>
        <Tag>Get Started</Tag>
        <Heading>Have an Application Idea?</Heading>
        <Lead style={{ margin: '0 auto 2rem' }}>
          From startup MVPs to enterprise platforms, we turn your vision into reality.
        </Lead>
        <BtnPrimary to="/contact">Discuss Your Project →</BtnPrimary>
      </Section> */}
    </>
  );
}
